"use client";

import * as React from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils.js";

/**
 * SafeCloseButton - A context-independent close button
 * 
 * This component can be used anywhere without requiring a specific context.
 * It's a safe alternative to DialogClose, SheetClose, etc.
 */
export interface SafeCloseButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Callback when the close button is clicked */
  onClose?: () => void;
  /** Whether to show the X icon */
  showIcon?: boolean;
  /** Custom icon component */
  icon?: React.ReactNode;
  /** Size variant */
  size?: "sm" | "md" | "lg";
  /** Variant style */
  variant?: "default" | "ghost" | "outline";
}

export const SafeCloseButton = React.forwardRef<
  HTMLButtonElement,
  SafeCloseButtonProps
>(({
  onClose,
  showIcon = true,
  icon,
  size = "md",
  variant = "default",
  className,
  children,
  onClick,
  ...props
}, ref) => {
  const handleClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    // Call the provided onClick first
    onClick?.(event);
    
    // Then call onClose if provided
    if (onClose && !event.defaultPrevented) {
      onClose();
    }
  }, [onClick, onClose]);

  const sizeClasses = {
    sm: "h-6 w-6 p-0",
    md: "h-8 w-8 p-0", 
    lg: "h-10 w-10 p-1"
  };

  const variantClasses = {
    default: "opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
    ghost: "hover:bg-accent hover:text-accent-foreground",
    outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground"
  };

  const iconSize = {
    sm: "h-3 w-3",
    md: "h-4 w-4",
    lg: "h-5 w-5"
  };

  return (
    <button
      ref={ref}
      type="button"
      onClick={handleClick}
      className={cn(
        "rounded-sm disabled:pointer-events-none",
        sizeClasses[size],
        variantClasses[variant],
        className
      )}
      aria-label="Close"
      {...props}
    >
      {showIcon && (icon || <X className={iconSize[size]} />)}
      {children}
      <span className="sr-only">Close</span>
    </button>
  );
});

SafeCloseButton.displayName = "SafeCloseButton";

/**
 * Hook to create a safe close handler
 */
export function useSafeClose(closeFn?: () => void) {
  return React.useCallback(() => {
    closeFn?.();
  }, [closeFn]);
}

/**
 * Higher-order component that wraps any component with safe close functionality
 */
export function withSafeClose(
  Component: React.ComponentType<any>
) {
  const WrappedComponent = React.forwardRef<any, any>((props, ref) => {
    const safeClose = useSafeClose(props.onClose);
    
    return (
      <Component
        {...props}
        onClose={safeClose}
      />
    );
  });
  
  WrappedComponent.displayName = `withSafeClose(${Component.displayName || Component.name})`;
  
  return WrappedComponent;
}
